/**
 * DepartureWizardModal - Multi-step employee departure wizard
 * Step 1: Review active assignments
 * Step 2: Select transfer target
 * Step 3: Confirm & execute
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { media } from '../../styles/media';
import { AlertTriangle, CheckCircle } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import memberService from '../../services/api/memberService';
import userService, { Member } from '../../services/api/userService';
import { extractApiError } from '../../utils/apiError';
import { getDescendantIds } from './supervisionSubtree';

// ============================================================================
// Props
// ============================================================================

interface DepartureWizardModalProps {
  member: Member;
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;
  background: ${({ theme, $active, $done }) => {
    if ($done) return theme.colors.status.success.bg;
    if ($active) return theme.colors.primary.base;
    return theme.colors.backgrounds.gray;
  }};
  color: ${({ theme, $active, $done }) => {
    if ($done) return theme.colors.status.success.text;
    if ($active) return theme.colors.text.white;
    return theme.colors.text.secondary;
  }};
  border: 1px solid
    ${({ theme, $active, $done }) => {
      if ($done) return theme.colors.status.success.border;
      if ($active) return theme.colors.primary.base;
      return theme.colors.borders.light;
    }};
`;

const StepConnector = styled.div<{ $done: boolean }>`
  flex: 1;
  height: 2px;
  background: ${({ theme, $done }) =>
    $done ? theme.colors.status.success.border : theme.colors.borders.light};
`;

const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
`;

const AssignmentCard = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  ${media.desktop} {
    grid-template-columns: 1fr;
  }
`;

const StatBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
`;

const StatNumber = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const InfoNote = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const InfoNoteSpaced = styled(InfoNote)`
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const SelectField = styled.select`
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const TransferSummaryBox = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const TransferSummaryTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const TransferSummaryText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const ResultBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const ResultIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ResultTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const ResultSubtext = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ReasonField = styled.textarea`
  width: 100%;
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  resize: vertical;
  min-height: 80px;
  font-family: inherit;
  margin-top: ${({ theme }) => theme.spacing[4]};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const FooterRight = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

const LoadingState = styled.div`
  padding: ${({ theme }) => theme.spacing[12]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
`;

// ============================================================================
// Component
// ============================================================================

type Step = 1 | 2 | 3 | 4; // 4 = done

export const DepartureWizardModal = ({ member, isOpen, onClose }: DepartureWizardModalProps) => {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(1);
  const [transferToId, setTransferToId] = useState('');
  const [reason, setReason] = useState('');
  const [departResult, setDepartResult] = useState<{
    transferredWorkflows: number;
    transferredClients: number;
    transferredSupervisees: number;
  } | null>(null);
  const [executeError, setExecuteError] = useState<string | null>(null);

  // Fetch active assignments
  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['member-active-assignments', member.id],
    queryFn: () => memberService.getActiveAssignments(member.id),
    enabled: isOpen && step === 1,
  });

  // Fetch active members for transfer target
  const { data: membersData } = useQuery({
    queryKey: ['members-all'],
    queryFn: () => userService.getMembers({ limit: 200, status: 'active' }),
    enabled: isOpen && step === 2,
  });

  // Exclude the departing member AND their whole subtree. Repointing their reports onto
  // someone who reports back into them would create a supervision cycle, which the server
  // rejects (#505) — so do not offer the choice in the first place.
  const allMembers = useMemo(() => membersData?.data ?? [], [membersData]);
  const eligibleMembers = useMemo(() => {
    const departingSubtree = getDescendantIds(allMembers, member.id);
    return allMembers.filter((m) => m.id !== member.id && !departingSubtree.has(m.id));
  }, [allMembers, member.id]);

  const departMutation = useMutation({
    mutationFn: () =>
      memberService.departMember(member.id, {
        transferToMemberId: transferToId || undefined,
        reason: reason.trim() || undefined,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      setDepartResult({
        transferredWorkflows: result.transferred.workflows,
        transferredClients: result.transferred.clients,
        transferredSupervisees: result.transferred.supervisees,
      });
      setStep(4);
    },
    onError: (error) => {
      // Surface the server's reason (e.g. the #505 "target reports to the departing
      // member" rejection) instead of swallowing it behind a generic retry prompt.
      setExecuteError(extractApiError(error, 'Departure failed. Please try again.'));
    },
  });

  const handleClose = () => {
    setStep(1);
    setTransferToId('');
    setReason('');
    setDepartResult(null);
    setExecuteError(null);
    onClose();
  };

  const workflowCount = assignments?.activeWorkflows.count ?? 0;
  const clientCount = assignments?.assignedClients.count ?? 0;
  const superviseeCount = assignments?.supervisees.count ?? 0;

  const hasAnyAssignments = workflowCount > 0 || clientCount > 0 || superviseeCount > 0;

  const selectedTransferTarget = eligibleMembers.find((m) => m.id === transferToId);

  const getTitle = () => {
    if (step === 4) return 'Departure Complete';
    return `Departure Wizard — ${member.firstName} ${member.lastName}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={getTitle()} size="md">
      {/* Step Indicator (steps 1–3) */}
      {step < 4 && (
        <StepIndicator>
          <StepDot $active={step === 1} $done={step > 1}>
            {step > 1 ? '✓' : '1'}
          </StepDot>
          <StepConnector $done={step > 1} />
          <StepDot $active={step === 2} $done={step > 2}>
            {step > 2 ? '✓' : '2'}
          </StepDot>
          <StepConnector $done={step > 2} />
          <StepDot $active={step === 3} $done={step > 3}>
            3
          </StepDot>
        </StepIndicator>
      )}

      {/* ── Step 1: Review Assignments ── */}
      {step === 1 && (
        <>
          <SectionTitle>Review Active Assignments</SectionTitle>

          {assignmentsLoading ? (
            <LoadingState>Loading assignments...</LoadingState>
          ) : (
            <>
              <AssignmentCard>
                <StatBox>
                  <StatNumber>{workflowCount}</StatNumber>
                  <StatLabel>Active Tasks</StatLabel>
                </StatBox>
                <StatBox>
                  <StatNumber>{clientCount}</StatNumber>
                  <StatLabel>Assigned Clients</StatLabel>
                </StatBox>
                <StatBox>
                  <StatNumber>{superviseeCount}</StatNumber>
                  <StatLabel>Supervisees</StatLabel>
                </StatBox>
              </AssignmentCard>

              {!hasAnyAssignments && (
                <InfoNote>
                  No active assignments found. You can proceed directly to confirm departure.
                </InfoNote>
              )}
            </>
          )}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={() => setStep(hasAnyAssignments ? 2 : 3)}
                disabled={assignmentsLoading}
              >
                Next
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 2: Select Transfer Target ── */}
      {step === 2 && (
        <>
          <SectionTitle>Select Transfer Target</SectionTitle>
          <FieldLabel htmlFor="transfer-target">
            Transfer assignments to:
          </FieldLabel>
          <SelectField
            id="transfer-target"
            value={transferToId}
            onChange={(e) => setTransferToId(e.target.value)}
          >
            <option value="">Select a member...</option>
            {eligibleMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
                {m.roles?.[0] ? ` — ${m.roles[0].name}` : ''}
              </option>
            ))}
          </SelectField>

          {selectedTransferTarget && (
            <InfoNoteSpaced>
              All tasks, clients, and supervisees will be reassigned to{' '}
              <strong>
                {selectedTransferTarget.firstName} {selectedTransferTarget.lastName}
              </strong>
              {selectedTransferTarget.roles?.[0]
                ? ` (${selectedTransferTarget.roles[0].name})`
                : ''}
              .
            </InfoNoteSpaced>
          )}

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep(1)}>
              Back
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={() => setStep(3)}
                disabled={!transferToId}
              >
                Next
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 3: Confirm & Execute ── */}
      {step === 3 && (
        <>
          <SectionTitle>Confirm Departure</SectionTitle>

          <TransferSummaryBox>
            <TransferSummaryTitle>
              <AlertTriangle />
              This action cannot be undone
            </TransferSummaryTitle>
            <TransferSummaryText>
              {hasAnyAssignments && selectedTransferTarget ? (
                <>
                  Transfer {workflowCount} task
                  {workflowCount !== 1 ? 's' : ''}, {clientCount} client
                  {clientCount !== 1 ? 's' : ''}, and {superviseeCount} supervisee
                  {superviseeCount !== 1 ? 's' : ''} to{' '}
                  <strong>
                    {selectedTransferTarget.firstName} {selectedTransferTarget.lastName}
                  </strong>
                  {' '}and deactivate{' '}
                </>
              ) : (
                <>Deactivate </>
              )}
              <strong>
                {member.firstName} {member.lastName}
              </strong>
              .
            </TransferSummaryText>
          </TransferSummaryBox>

          <FieldLabel htmlFor="departure-reason">Reason (optional)</FieldLabel>
          <ReasonField
            id="departure-reason"
            placeholder="Resignation, termination, contract end..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />

          {executeError && <ErrorText>{executeError}</ErrorText>}

          <ModalFooter>
            <Button variant="secondary" onClick={() => setStep(hasAnyAssignments ? 2 : 1)}>
              Back
            </Button>
            <FooterRight>
              <Button
                variant="danger"
                onClick={() => departMutation.mutate()}
                isLoading={departMutation.isPending}
                disabled={departMutation.isPending}
              >
                Confirm Departure
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 4: Done ── */}
      {step === 4 && departResult && (
        <>
          <ResultBanner>
            <ResultIcon>
              <CheckCircle />
            </ResultIcon>
            <ResultTitle>
              {member.firstName} {member.lastName} has departed
            </ResultTitle>
            <ResultSubtext>
              {departResult.transferredWorkflows} task
              {departResult.transferredWorkflows !== 1 ? 's' : ''},{' '}
              {departResult.transferredClients} client
              {departResult.transferredClients !== 1 ? 's' : ''}, and{' '}
              {departResult.transferredSupervisees} supervisee
              {departResult.transferredSupervisees !== 1 ? 's' : ''} transferred. Member
              deactivated.
            </ResultSubtext>
          </ResultBanner>

          <ModalFooter>
            <div />
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default DepartureWizardModal;
